import * as React from 'react';
import { Alert, Button, Stack } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { Institution, InstitutionParent, InstitutionView, ReportInstitutionView } from 'cb-model';
import {
  Display,
  IsAllowed,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  useCacheQuery,
} from 'gossamer-front-end';
import { getFilter } from 'gossamer-unified';
import { DeleteButton } from 'shared-front-end';
import { SummaryChartTable, useDateRange } from '../reporting';

export const InstitutionDisplay = (): React.JSX.Element => {
  const params = useParams();
  const institutionId = parseInt(params.key, 10);
  const reportingDateRange = useDateRange();
  const reportingQuery = useCacheQuery(ReportInstitutionView, {
    where: {
      and: [
        getFilter('institution_id', 'eq', institutionId),
        getFilter('week_commencing', 'gt', reportingDateRange.start.toISOString()),
        getFilter('week_commencing', 'lt', reportingDateRange.end.toISOString()),
      ],
    },
    order: [['week_commencing', 'ASC']],
  });

  return (
    <>
      <NavIconContainer entityId={InstitutionView.id} />
      <PageTitle
        verb="Display"
        entity={InstitutionView}
        keyValue={params.key}
        subtitle="Details relating to an Institution"
      />

      <Section>
        <Alert variant="info">
          This has changed! The reporting period now reflects the last twelve weeks of available data.
        </Alert>
        <SummaryChartTable
          data={reportingQuery.data || []}
          entity={ReportInstitutionView}
          period={reportingDateRange}
        />
      </Section>

      <Section>
        <QuerySingle entity={InstitutionView} keyValue={params.key} render={Display} />
        <Stack direction="horizontal" gap={2}>
          <IsAllowed entity={Institution} action="update">
            <Link to={`/admin/institution/update/${params.key}`}>
              <Button size="sm" variant="primary">
                Update
              </Button>
            </Link>
          </IsAllowed>
          <IsAllowed entity={Institution} action="delete">
            <DeleteButton
              entityId="Institution"
              keyValue={params.key}
              followOnUrl="/admin/institution/search"
              otherEntitiesToReset={['InstitutionView']}
            />
          </IsAllowed>
        </Stack>
      </Section>
      <Section title="Institution Child Units">
        <QueryMulti
          entity={InstitutionParent}
          render={List}
          showItemCount={false}
          showSettingsControl={false}
          showPaginator="never"
          queryOptions={{
            where: {
              and: [getFilter('parent_institution', 'eq', params.key)],
            },
          }}
        />
        <div className="mb20px" />
        {/* Intended for staff users only, for now */}
        <IsAllowed entity={InstitutionParent} action="update">
          <Link to={`/admin/institution/child-grid/${params.key}`}>
            <Button size="sm" variant="primary">
              Update
            </Button>
          </Link>
        </IsAllowed>
      </Section>
    </>
  );
};
